import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Link2, Funnel, Pencil, X, Users } from 'lucide-react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import ReusableDataTable from '../../components/table/ReusableDataTable';
import { getZellePaymentsList, getDeliveryDates, getPartnersList, assignPartnerToZellePayment, updateZellePaymentPhone } from '../../store/thunk/adminThunk';
import { formatPrice, formatToUSDate } from '../../hooks/commonFunction';
import toast from 'react-hot-toast';
import useIsMobile from '../../hooks/useIsMobile';
import OrderMappingModal from '../../components/admin/OrderMappingModal';

const ZellePaymentList = () => {
  const dispatch = useDispatch();
  const { coreSettings, userInfo } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(!isMobile);
  // State management
  const [zellePayments, setZellePayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [selectedZellePayment, setSelectedZellePayment] = useState(null);

  // Partner assignment state
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [selectedPaymentForPartner, setSelectedPaymentForPartner] = useState(null);
  const [partnersList, setPartnersList] = useState([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [isAssigningPartner, setIsAssigningPartner] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');

  // Phone edit state
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [selectedPaymentForPhone, setSelectedPaymentForPhone] = useState(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Date range state - will be populated from API
  const [defaultDateRange, setDefaultDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [activeFilters, setActiveFilters] = useState({});

  // Status filter configuration
  // Status filter configuration
  const [zelleFilters, setZelleFilters] = useState([
    {
      key: 'status',
      label: 'Status',
      options: [{ value: '', label: 'All Statuses' }],
    },
  ]);

  // Fetch partners list on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const result = await dispatch(getPartnersList()).unwrap();
        if (result?.data) {
          setPartnersList(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch partners list:', error);
      }
    };
    fetchPartners();
  }, [dispatch]);

  // Update filters when coreSettings or partnersList change
  useEffect(() => {
    const statusOptions = coreSettings?.ZELLE_PAYMENT_STATUS
      ? Object.entries(coreSettings.ZELLE_PAYMENT_STATUS).map(([key, value]) => ({
          value: key,
          label: value,
        }))
      : [];

    const partnerOptions = partnersList.map((partner) => ({
      value: partner.id || partner.partner_id,
      label: partner.partner_name || partner.name,
    }));

    setZelleFilters([
      {
        key: 'status',
        label: 'Status',
        options: [{ value: '', label: 'All Statuses' }, ...statusOptions],
      },
      {
        key: 'partnerId',
        label: 'Delivery Partner',
        options: [{ value: '', label: 'All Partners' }, ...partnerOptions],
      },
      {
        key: 'memo',
        label: 'Memo',
        type: 'text',
        placeholder: 'Search by Memo',
      },
    ]);
  }, [coreSettings, partnersList]);

  // Handle open partner assignment modal
  const handleOpenPartnerModal = async (row) => {
    setSelectedPaymentForPartner(row);
    setSelectedPartnerId(row.assigned_partner_id || row.partner_id || '');
    setIsPartnerModalOpen(true);
    setIsLoadingPartners(true);
    try {
      const result = await dispatch(getPartnersList()).unwrap();
      if (result?.data) {
        setPartnersList(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      toast.error('Failed to load partners list');
    } finally {
      setIsLoadingPartners(false);
    }
  };

  // Handle assign partner from dropdown
  const handleAssignPartner = async () => {
    if (!selectedPartnerId) {
      toast.error('Please select a partner');
      return;
    }
    setIsAssigningPartner(true);
    try {
      await dispatch(assignPartnerToZellePayment({
        zelle_payment_id: selectedPaymentForPartner.id,
        partner_id: selectedPartnerId,
      })).unwrap();
      const partner = partnersList.find((p) => (p.id || p.partner_id) == selectedPartnerId);
      toast.success(`Partner "${partner?.partner_name || partner?.name || ''}" assigned successfully`);
      setIsPartnerModalOpen(false);
      setSelectedPaymentForPartner(null);
      setSelectedPartnerId('');
      fetchZellePaymentsList(currentPage, rowsPerPage, activeFilters);
    } catch (error) {
      console.error('Failed to assign partner:', error);
      toast.error(error || 'Failed to assign partner');
    } finally {
      setIsAssigningPartner(false);
    }
  };

  // Handle open mapping modal
  const handleOpenMappingModal = (row) => {
    setSelectedZellePayment(row);
    setIsMappingModalOpen(true);
  };

  // Handle apply mappings
  const handleApplyMappings = (mappedOrders) => {
    console.log('Mapped orders for Zelle Payment:', selectedZellePayment?.id, mappedOrders);
    // TODO: Implement API call to save the mappings
    toast.success(`Successfully mapped ${mappedOrders.length} order(s)`);
    // Optionally refresh the list
    fetchZellePaymentsList(currentPage, rowsPerPage);
  };

  // Handle open phone edit modal
  const handleOpenPhoneModal = (row) => {
    setSelectedPaymentForPhone(row);
    setPhoneInput(row.phone_number || '');
    setPhoneError('');
    setIsPhoneModalOpen(true);
  };

  // Handle update phone number
  const handleUpdatePhone = async () => {
    const digits = phoneInput.replace(/\D/g, '');
    if (!digits || digits.length < 7) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }
    setIsUpdatingPhone(true);
    try {
      await dispatch(
        updateZellePaymentPhone({
          zelle_payment_id: selectedPaymentForPhone.id,
          phone_number: digits,
        }),
      ).unwrap();
      toast.success('Phone number updated successfully');
      setIsPhoneModalOpen(false);
      setSelectedPaymentForPhone(null);
      fetchZellePaymentsList(currentPage, rowsPerPage, activeFilters);
    } catch (error) {
      toast.error(error || 'Failed to update phone number');
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  // Define columns
  const columns = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '70px',
    },
    {
      name: 'Name',
      cell: (row) => <span className='font-medium'>{row.name || 'N/A'}</span>,
      sortable: true,
      width: '150px',
    },
    {
      name: 'Memo',
      cell: (row) => (
        <div className='text-xs max-w-xs truncate' title={row.memo}>
          {row.memo || 'N/A'}
        </div>
      ),
      sortable: false,
    },
    {
      name: 'Phone Number',
      cell: (row) => (
        <div className='flex items-center gap-1.5'>
          <span className='font-medium'>{row.phone_number || 'N/A'}</span>
          <button
            onClick={() => handleOpenPhoneModal(row)}
            className='p-1 text-gray-400 hover:text-primary-100 hover:bg-red-50 rounded transition-colors flex-shrink-0'
            title='Edit phone number'
          >
            <Pencil size={13} />
          </button>
        </div>
      ),
      sortable: true,
      width: '175px',
    },
    {
      name: 'Payment ID',
      cell: (row) => <span className='font-mono text-sm'>#{row.payment_id || 'N/A'}</span>,
      sortable: true,
      width: '110px',
    },
    {
      name: 'Amount',
      cell: (row) => <span className='font-medium'>{formatPrice(row.amount)}</span>,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Status',
      cell: (row) => {
        const isMapped = row.status === 'mapped' || row.status === 'processed' || row.status === 'completed';
        const isUnmapped = row.status === 'unmapped' || row.status === 'received' || !row.status;
        const isPartial = row.status === 'partial';
        
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              isUnmapped
                ? 'bg-yellow-100 text-yellow-800'
                : isMapped
                  ? 'bg-green-100 text-green-800'
                  : isPartial
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-red-800'
            }`}
          >
            {isMapped ? 'mapped' : isUnmapped ? 'unmapped' : row.status}
          </span>
        );
      },
      sortable: true,
    },
    {
      name: 'Transaction ID',
      cell: (row) => <span className='font-mono text-xs'>{row.transaction_id || 'N/A'}</span>,
      sortable: true,
    },
    {
      name: 'Transaction Date',
      cell: (row) => formatToUSDate(row.transaction_date),
      sortable: true,
    },

    ...(userInfo?.user_role === 1 ? [{
      name: 'Assign Partner',
      cell: (row) => (
        <div className='flex items-center gap-1.5'>
          {row.assigned_partner_name || row.partner_name ? (
            <>
              <span className='text-xs font-medium text-gray-800 truncate max-w-[80px]' title={row.assigned_partner_name || row.partner_name}>
                {row.assigned_partner_name || row.partner_name}
              </span>
              <button
                onClick={() => handleOpenPartnerModal(row)}
                className='p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0'
                title='Change partner'
              >
                <Pencil size={13} />
              </button>
            </>
          ) : (
            <button
              onClick={() => handleOpenPartnerModal(row)}
              className='px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 bg-primary-100 text-white'
              title='Assign partner'
            >
              <Users size={14} />
              Assign
            </button>
          )}
        </div>
      ),
      width: '150px',
    }] : []),
    {
      name: 'Created At',
      cell: (row) => formatToUSDate(row.created_at),
      sortable: true,
    },
    {
      name: 'Mappings',
      cell: (row) => {
        const isMapped = row.status === 'processed' || row.status === 'mapped' || row.status === 'completed';
        return (
          <button
            onClick={() => handleOpenMappingModal(row)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
              isMapped
                ? 'bg-green-600 text-white'
                : 'bg-primary-100 text-white '
            }`}
            title={
              isMapped ? 'Already mapped' : 'Map orders to this Zelle payment'
            }
          >
            <Link2 size={14} />
            {isMapped ? 'Mapped' : 'Mapping'}
          </button>
        );
      },
      width: '120px',
    },
  ];

  // Fetch Zelle payments list
  const fetchZellePaymentsList = async (page = 1, limit = 10, filters = {}) => {
    try {
      setIsLoading(true);

      // Merge with active filters
      const mergedFilters = Object.keys(filters).length > 0 ? filters : activeFilters;

      const params = {
        page,
        limit,
      };

      // Only include date range if explicitly set in filters
      if (mergedFilters.startDate) {
        params.start_date = mergedFilters.startDate;
      }

      if (mergedFilters.endDate) {
        params.end_date = mergedFilters.endDate;
      }

      if (mergedFilters.status) {
        params.status = mergedFilters.status;
      }

      if (mergedFilters.partnerId) {
        params.partner_id = mergedFilters.partnerId;
      }

      if (mergedFilters.memo) {
        params.memo = mergedFilters.memo;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Pass partner_id for role 6 users
    //   if (userInfo?.user_role === 6 && userInfo?.pickup_id) {
    //     params.partner_id = userInfo.pickup_id;
    //   }

      const result = await dispatch(getZellePaymentsList(params)).unwrap();

      if (result?.status === 1 && result?.data) {
        let filteredData = result.data.zellePayments || result.data.payments || result.data || [];

        // Apply search filter
        if (searchQuery) {
          const searchLower = searchQuery.toLowerCase();
          filteredData = filteredData.filter((item) => {
            return (
              item.name?.toLowerCase().includes(searchLower) ||
              item.payment_id?.toString().includes(searchLower) ||
              item.transaction_id?.toString().toLowerCase().includes(searchLower) ||
              item.amount?.toString().includes(searchLower) ||
              item.status?.toLowerCase().includes(searchLower) ||
              item.memo?.toLowerCase().includes(searchLower) ||
              item.error_message?.toLowerCase().includes(searchLower)
            );
          });
        }

        setZellePayments(filteredData);
        setTotalRows(result.data.pagination?.total || filteredData.length);
        setCurrentPage(result.data.pagination?.page || page);
      }
    } catch (error) {
      console.error('Failed to fetch Zelle payments list:', error);
      toast.error('Failed to fetch Zelle payments list');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch delivery dates from API and set default date range
  useEffect(() => {
    const fetchDeliveryDates = async () => {
      try {
        const result = await dispatch(getDeliveryDates()).unwrap();
        if (result?.data?.length > 0) {
          // Use the first date as both start and end date
          const firstDate = result.data[0].date;
          setDefaultDateRange({
            startDate: firstDate,
            endDate: firstDate,
          });
        }
      } catch (error) {
        console.error('Failed to fetch delivery dates:', error);
      }
    };

    fetchDeliveryDates();
  }, [dispatch]);

  // Initial load - only fetch when date range is loaded
  useEffect(() => {
    // Only fetch payments once delivery dates are loaded
    if (!defaultDateRange.startDate || !defaultDateRange.endDate) {
      return;
    }
    // Set default dates as active filters so they are passed in the first request (With filters)
    setActiveFilters(defaultDateRange);
    fetchZellePaymentsList(1, rowsPerPage, defaultDateRange);
  }, [defaultDateRange]);

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchZellePaymentsList(1, rowsPerPage, activeFilters);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchZellePaymentsList(page, rowsPerPage, activeFilters);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
    fetchZellePaymentsList(page, newPerPage, activeFilters);
  };

  // Handle filter change
  const handleFilterChange = async (filters) => {
    setCurrentPage(1);

    const filterParams = {
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      status: filters.status || '',
      partnerId: filters.partnerId || '',
      memo: filters.memo || '',
    };

    setActiveFilters(filterParams);
    await fetchZellePaymentsList(1, rowsPerPage, filterParams);
  };

  return (
    <section className='overflow-y-auto relative no-scrollbar'>
      <div className='container px-0'>
        <div className='p-4'>
          <div className='py-5 bg-neutral-100 px-5 mb-5 rounded-lg'>
            {/* Header Section */}
            <div className='flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
              <h2 className='text-xl font-semibold'>Zelle Payment List</h2>

              {/* Search Controls */}
              <div className='flex gap-2 items-center'>
                {/* Search */}
                <div className='relative flex-1 md:w-60'>
                  <input
                    type='text'
                    placeholder='Search by name, payment ID, transaction ID...'
                    value={searchQuery}
                    onChange={handleSearch}
                    className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm pr-2 pl-10 outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent hover:border-gray-400 transition-colors w-full'
                  />
                  <Search className='absolute top-1/2 left-4 -translate-y-1/2 h-4 w-4 text-primary-500' />
                </div>
                <button
                  className='admin-outline-btn md:flex-none px-3'
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Funnel className='admin-outline-btn-icon' />
                </button>
              </div>
            </div>
          </div>

          <ReusableDataTable
            columns={columns}
            data={zellePayments}
            filters={zelleFilters}
            isLoading={isLoading}
            showCheckbox={false}
            showDateRangeFilter={true}
            defaultDateRange={defaultDateRange}
            dateStartLabel='Start Date'
            dateEndLabel='End Date'
            onFilterChange={handleFilterChange}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </div>
      </div>

      {/* Order Mapping Modal */}
      <OrderMappingModal
        open={isMappingModalOpen}
        setOpen={setIsMappingModalOpen}
        onApply={handleApplyMappings}
        zellePaymentDetails={selectedZellePayment}
      />

      {/* Partner Assignment Modal */}
      <Dialog open={isPartnerModalOpen} onClose={() => setIsPartnerModalOpen(false)} className='relative z-50'>
        <DialogBackdrop className='fixed inset-0 bg-black/50' />
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <DialogPanel className='bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col'>
            {/* Header */}
            <div className='flex items-center justify-between bg-red-gradient p-4'>
              <DialogTitle className='text-sm sm:text-md font-normal text-white flex items-center gap-2'>
                <Users className='h-5 w-5' />
                <span>Assign Partner</span>
              </DialogTitle>
              <button
                onClick={() => setIsPartnerModalOpen(false)}
                className='text-white hover:text-gray-200 transition-colors'
              >
                <X size={22} />
              </button>
            </div>

            {/* Payment Info */}
            {selectedPaymentForPartner && (
              <div className='px-4 pt-4 pb-2'>
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                  <div className='grid grid-cols-2 gap-2 text-sm'>
                    <div>
                      <span className='text-gray-500 text-xs'>Payment ID</span>
                      <p className='font-semibold text-primary-100'>#{selectedPaymentForPartner.id}</p>
                    </div>
                    <div>
                      <span className='text-gray-500 text-xs'>Name</span>
                      <p className='font-medium text-gray-900'>{selectedPaymentForPartner.name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className='text-gray-500 text-xs'>Amount</span>
                      <p className='font-bold text-green-700'>{formatPrice(selectedPaymentForPartner.amount)}</p>
                    </div>
                    <div>
                      <span className='text-gray-500 text-xs'>Current Partner</span>
                      <p className='font-medium text-gray-900'>
                        {selectedPaymentForPartner.assigned_partner_name || selectedPaymentForPartner.partner_name || 'None'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Partner Select Dropdown */}
            <div className='px-4 py-4'>
              <label className='block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2'>
                Select a Partner
              </label>
              {isLoadingPartners ? (
                <div className='flex items-center justify-center py-6'>
                  <svg className='animate-spin h-5 w-5 text-primary-100' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                  </svg>
                  <span className='ml-2 text-sm text-gray-600'>Loading partners...</span>
                </div>
              ) : (
                <>
                  <select
                    value={selectedPartnerId}
                    onChange={(e) => setSelectedPartnerId(e.target.value)}
                    disabled={isAssigningPartner}
                    className='w-full border-2 border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <option value=''>-- Select Partner --</option>
                    {partnersList.map((partner) => {
                      const partnerId = partner.id || partner.partner_id;
                      const partnerName = partner.partner_name || partner.name;
                      return (
                        <option key={partnerId} value={partnerId}>
                          {partnerName}
                        </option>
                      );
                    })}
                  </select>

                  <button
                    onClick={handleAssignPartner}
                    disabled={!selectedPartnerId || isAssigningPartner}
                    className={`w-full mt-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                      !selectedPartnerId || isAssigningPartner
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'main-btn hover:opacity-90'
                    }`}
                  >
                    {isAssigningPartner ? 'Assigning...' : 'Assign Partner'}
                  </button>
                </>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      {/* Edit Phone Number Modal */}
      <Dialog open={isPhoneModalOpen} onClose={() => setIsPhoneModalOpen(false)} className='relative z-50'>
        <DialogBackdrop className='fixed inset-0 bg-black/50' />
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <DialogPanel className='bg-white rounded-lg shadow-xl w-full max-w-sm'>
            {/* Header */}
            <div className='flex items-center justify-between bg-red-gradient px-4 py-3 rounded-t-lg'>
              <DialogTitle className='text-sm font-medium text-white flex items-center gap-2'>
                <Pencil size={16} />
                Edit Phone Number
              </DialogTitle>
              <button
                onClick={() => setIsPhoneModalOpen(false)}
                className='text-white hover:text-gray-200 transition-colors'
              >
                <X size={20} />
              </button>
            </div>

            <div className='p-5'>
              {/* Current Info */}
              {selectedPaymentForPhone && (
                <div className='bg-gray-50 rounded-lg p-3 mb-4 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>Payment ID</span>
                    <span className='font-semibold text-primary-100'>#{selectedPaymentForPhone.id}</span>
                  </div>
                  <div className='flex justify-between mt-1'>
                    <span className='text-gray-500'>Name</span>
                    <span className='font-medium'>{selectedPaymentForPhone.name || 'N/A'}</span>
                  </div>
                  <div className='flex justify-between mt-1'>
                    <span className='text-gray-500'>Current Phone</span>
                    <span className='font-medium'>{selectedPaymentForPhone.phone_number || 'N/A'}</span>
                  </div>
                </div>
              )}

              {/* Phone Input */}
              <label className='block text-xs font-medium text-gray-600 mb-1.5'>
                New Phone Number
              </label>
              <input
                type='tel'
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  setPhoneError('');
                }}
                placeholder='Enter phone number'
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-700 transition-colors ${
                  phoneError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'
                }`}
              />
              {phoneError && (
                <p className='text-xs text-red-500 mt-1'>{phoneError}</p>
              )}

              {/* Actions */}
              <div className='flex gap-3 mt-5'>
                <button
                  onClick={() => setIsPhoneModalOpen(false)}
                  disabled={isUpdatingPhone}
                  className='flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePhone}
                  disabled={isUpdatingPhone}
                  className='flex-1 px-4 py-2 rounded-lg bg-primary-100 text-white text-sm font-medium hover:bg-red-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2'
                >
                  {isUpdatingPhone ? (
                    <>
                      <svg className='animate-spin h-4 w-4' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </section>
  );
};

export default ZellePaymentList;
